import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { userService } from '../../services/userService';

const UserForm = ({ open, onClose, user, editMode, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'User',
    manager_id: null,
  });

  const [superUsers, setSuperUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    loadSuperUsers();
    if (editMode && user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        role: user.role || 'User',
        manager_id: user.manager_id || null,
      });
    }
  }, [editMode, user]);

  const loadSuperUsers = async () => {
    try {
      const data = await userService.getSuperUsers();
      setSuperUsers(data);
    } catch (err) {
      console.error('Failed to load SuperUsers:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setValidationError('');
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      setValidationError('Username is required');
      return false;
    }

    if (!formData.email.trim()) {
      setValidationError('Email is required');
      return false;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setValidationError('Invalid email format');
      return false;
    }

    // Password validation only for new users or when changing password
    if (!editMode || formData.password) {
      if (!formData.password) {
        setValidationError('Password is required');
        return false;
      }

      if (formData.password.length < 6) {
        setValidationError('Password must be at least 6 characters');
        return false;
      }

      if (formData.password !== formData.confirmPassword) {
        setValidationError('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        manager_id: formData.manager_id || null,
      };

      // Only include password if it's set
      if (formData.password) {
        submitData.password = formData.password;
      }

      if (editMode) {
        // Update existing user
        await userService.update(user.user_id, submitData);
        onSuccess(`User "${formData.username}" updated successfully`);
      } else {
        // Create new user
        await userService.create(submitData);
        onSuccess(`User "${formData.username}" created successfully`);
      }

      onClose(true); // Close with reload
    } catch (err) {
      onError(editMode ? 'Failed to update user: ' + err.message : 'Failed to create user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editMode ? 'Edit User' : 'Create New User'}
          </Typography>
          <IconButton onClick={() => onClose(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Username */}
            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              fullWidth
              autoFocus={!editMode}
              disabled={editMode} // Username cannot be changed
              helperText={editMode ? 'Username cannot be changed' : ''}
            />

            {/* Email */}
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
            />

            {/* Password */}
            <TextField
              label={editMode ? 'New Password (leave blank to keep current)' : 'Password'}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required={!editMode}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText="Minimum 6 characters"
            />

            {/* Confirm Password */}
            {(!editMode || formData.password) && (
              <TextField
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                required={!editMode || !!formData.password}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Role */}
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={formData.role}
                label="Role"
                onChange={handleChange}
              >
                <MenuItem value="Admin">Admin - Full system access</MenuItem>
                <MenuItem value="PowerUser">Power User - View/Edit assigned projects</MenuItem>
                <MenuItem value="SuperUser">Super User - Manage team + assigned projects</MenuItem>
                <MenuItem value="User">User - View/Edit assigned projects only</MenuItem>
              </Select>
            </FormControl>

            {/* Manager (only for regular Users) */}
            {formData.role === 'User' && (
              <FormControl fullWidth>
                <InputLabel>Manager (SuperUser)</InputLabel>
                <Select
                  name="manager_id"
                  value={formData.manager_id || ''}
                  label="Manager (SuperUser)"
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>No Manager</em>
                  </MenuItem>
                  {superUsers.map((su) => (
                    <MenuItem key={su.user_id} value={su.user_id}>
                      {su.username} ({su.email})
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  SuperUsers can assign their projects to subordinates
                </Typography>
              </FormControl>
            )}

            {/* Info box */}
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Note:</strong> After creating the user, you can assign projects to them via
                the "Assign Projects" action.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => onClose(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 3,
            }}
          >
            {loading ? 'Saving...' : editMode ? 'Update User' : 'Create User'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserForm;
